import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import aboutPortrait from "../assets/about-dr-fiza.png";

const stats = [
  { value: 18, suffix: "+", label: "Years Experience" },
  { value: 5, suffix: "L+", label: "Happy Clients" },
  { value: 4, suffix: "", label: "Specialized Domains" },
  { value: 1000, suffix: "+", label: "Workshops Conducted" },
];

function Counter({ target, suffix, active }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const duration = 1800;
    const startTime = performance.now();
    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setN(Math.floor(start + (target - start) * eased));
      if (progress < 1) requestAnimationFrame(tick);
      else setN(target);
    };
    requestAnimationFrame(tick);
  }, [active, target]);
  return (
    <span>
      {n}
      {suffix}
    </span>
  );
}

const portraitFrameStyle = {
  boxShadow: "0 12px 30px -16px rgba(31,51,41,0.18)",
  border: "1px solid #e3efe9",
};

export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section
      id="about"
      data-testid="about-section"
      ref={ref}
      className="relative py-24 md:py-32 px-6"
      style={{
        background:
          "linear-gradient(180deg, #f7faf8 0%, #e9f3ee 50%, #f7faf8 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 md:gap-12 lg:gap-16 items-stretch">
          {/* Left column — heading + introduction */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            data-testid="about-bio"
            className="flex flex-col"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7 }}
              className="mb-10 md:mb-12 text-center md:text-left"
            >
              <p
                className="font-body italic tracking-[0.3em] text-xs uppercase mb-4"
                style={{ color: "var(--color-accent)" }}
              >
                — Get to know —
              </p>
              <h2
                className="font-display font-bold"
                style={{
                  fontSize: "clamp(2rem, 5vw, 3.25rem)",
                  color: "var(--color-text-dark)",
                }}
                data-testid="about-title"
              >
                ABOUT DR. FIZA HASNY
              </h2>
              <div
                className="w-20 h-[3px] mt-6 rounded-full mx-auto md:mx-0"
                style={{ background: "var(--color-accent)" }}
              />
            </motion.div>

            <h3
              className="font-display text-2xl md:text-3xl mb-6 leading-snug text-center md:text-left"
              style={{ color: "var(--color-text-dark)" }}
            >
              A compassionate space for
              <span style={{ color: "var(--color-accent)" }}> healing </span>
              and growth.
            </h3>
            <p
              className="font-body text-base md:text-lg leading-relaxed mb-5 text-center md:text-left"
              style={{ color: "var(--color-text-light)" }}
            >
              Dr. Fiza Hasny is a Consultant Clinical Psychologist with over 18
              years of experience in the assessment and treatment of a wide range
              of psychological and emotional concerns. She holds a Ph.D. and
              M.Phil. in Clinical Psychology and is committed to providing
              compassionate, evidence-based psychological care.
            </p>
            <p
              className="font-body text-base md:text-lg leading-relaxed mb-5 text-center md:text-left"
              style={{ color: "var(--color-text-light)" }}
            >
              In addition to her clinical practice, Dr. Fiza is an active
              researcher with publications in reputed international peer-reviewed
              journals. She is also the author of the bestselling book The
              Blissful Mind, reflecting her commitment to making psychological
              knowledge accessible, practical, and meaningful.
            </p>
            <p
              className="font-body text-base md:text-lg leading-relaxed mb-5 text-center md:text-left"
              style={{ color: "var(--color-text-light)" }}
            >
              Dr. Fiza works with children, adolescents, adults, couples, and
              families, offering personalized psychological care in a safe,
              confidential, and supportive environment. Her approach combines
              scientific expertise with empathy to help clients build resilience,
              navigate life's challenges, and create meaningful, lasting change.
            </p>
            <p
              className="font-body text-base md:text-lg leading-relaxed text-center md:text-left"
              style={{ color: "var(--color-text-light)" }}
            >
              "Every individual has the capacity to heal, grow, and flourish.
              With the right support, insight, and evidence-based care, lasting
              transformation is possible."
            </p>
          </motion.div>

          {/* Right column — portrait (desktop / tablet) */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="hidden md:flex min-h-0"
            data-testid="about-portrait"
          >
            <div
              className="relative w-full min-h-[360px] md:min-h-[420px] h-full overflow-hidden rounded-2xl bg-white"
              style={portraitFrameStyle}
            >
              <img
                src={aboutPortrait}
                alt="Dr. Fiza Hasny — Psychologist in Jaipur"
                className="absolute inset-0 h-full w-full object-cover"
                style={{ objectPosition: "center 12%" }}
                width={560}
                height={700}
                loading="lazy"
                decoding="async"
              />
            </div>
          </motion.div>
        </div>

        {/* Mobile portrait — between intro and stats */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="md:hidden mt-10"
          data-testid="about-portrait-mobile"
        >
          <div
            className="relative mx-auto w-full max-w-sm overflow-hidden rounded-2xl bg-white"
            style={{
              ...portraitFrameStyle,
              aspectRatio: "4 / 5",
            }}
          >
            <img
              src={aboutPortrait}
              alt="Dr. Fiza Hasny — Psychologist in Jaipur"
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: "center 12%" }}
              width={400}
              height={500}
              loading="lazy"
              decoding="async"
            />
          </div>
        </motion.div>

        {/* Statistics cards */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-12 md:mt-14 lg:mt-16 grid grid-cols-2 gap-5 md:gap-6"
          data-testid="about-stats"
        >
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
              className="rounded-2xl p-6 md:p-8 text-center bg-white"
              style={{
                boxShadow: "0 12px 30px -16px rgba(31,51,41,0.18)",
                border: "1px solid #e3efe9",
              }}
              data-testid={`stat-card-${i}`}
            >
              <div
                className="font-display font-bold leading-none"
                style={{
                  fontSize: "clamp(2.25rem, 5vw, 3.5rem)",
                  color: "var(--color-accent)",
                }}
              >
                <Counter target={s.value} suffix={s.suffix} active={inView} />
              </div>
              <p
                className="font-body mt-3 text-sm md:text-base"
                style={{ color: "var(--color-text-light)" }}
              >
                {s.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
